import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, Paper, Skeleton } from "@mui/material";
import { AccountBalanceOutlined, BookmarkBorder, Groups } from "@mui/icons-material";
import { DocumentList, ExpProcessRow, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setExpedienteEmpresaDoc } from "@store/slices/expedienteSlice";
import { validateRequiredDocumentsAreUploaded } from "@components/TabBofu/functions";
import { isFormStatusEditable } from "@helpers/funciones";
import { PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";

const BASE = PATHS_EXPEDIENTE_V2.expediente.path;

export const TabEmpresa = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const empresa = useSelector((state: RootState) => state.expediente.empresa);
  const isLoading = useSelector((state: RootState) => !state.expediente.tabsReady);

  const documentos = empresa.documents;

  const {
    solicitud_credito: showSolicitudCredito,
    referencias_personales: showReferencias,
    informacion_bancaria: showInformacionBancaria,
  } = empresa.forms;

  const showAnyEmpresaForm = showSolicitudCredito.enabled || showReferencias || showInformacionBancaria;

  const garantiaActiva = useSelector((state: RootState) => state.expediente.tabs.garantia.is_active);
  const personasActiva = useSelector((state: RootState) => state.expediente.tabs.personas.is_active);
  const nextTab = garantiaActiva ? "garantia" : "personas";

  const formsCompletos =
    (!showSolicitudCredito.enabled || !isFormStatusEditable(application.loan_application_information_status)) &&
    (!showReferencias || !isFormStatusEditable(application.references_status)) &&
    (!showInformacionBancaria || !isFormStatusEditable(application.bank_information_status));

  const isComplete = !isLoading && validateRequiredDocumentsAreUploaded(documentos) && formsCompletos && personasActiva;

  const PATH_SOLICITUD = PATHS_EXPEDIENTE_V2.informacionSolicitud.path;
  const PATH_REFERENCIAS = PATHS_EXPEDIENTE_V2.referencias.path;
  const PATH_INFO_BANCARIA = PATHS_EXPEDIENTE_V2.informacionBancaria.path;

  return (
    <Box>
      {isLoading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
            {showAnyEmpresaForm && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Paper variant="lendia_paper" sx={{ p: "18px 20px" }}>
                  {showSolicitudCredito.enabled && (
                    <>
                      <ExpProcessRow
                        heading="Información de la solicitud"
                        hint="Proporciona tu contacto, transaccionales y declaraciones regulatorias."
                        status={application.loan_application_information_status}
                        icon={<BookmarkBorder sx={{ fontSize: 18 }} />}
                        label="LLenar"
                        onClick={() => navigate(PATH_SOLICITUD)}
                        disabled={!isFormStatusEditable(application.loan_application_information_status)}
                        data-testid="expediente-empresa-solicitud-button"
                      />
                      {(showReferencias || showInformacionBancaria) && <Divider sx={{ my: "18px" }} />}
                    </>
                  )}

                  {showReferencias && (
                    <>
                      <ExpProcessRow
                        heading="Referencias comerciales"
                        hint="Clientes y proveedores que podamos contactar para validar tu operación."
                        status={application.references_status}
                        icon={<Groups sx={{ fontSize: 18 }} />}
                        label="Llenar"
                        onClick={() => navigate(PATH_REFERENCIAS)}
                        disabled={!isFormStatusEditable(application.references_status)}
                        data-testid="expediente-empresa-referencias-button"
                      />
                      {showInformacionBancaria && <Divider sx={{ my: "18px" }} />}
                    </>
                  )}

                  {showInformacionBancaria && (
                    <ExpProcessRow
                      heading="Información bancaria"
                      hint="Cuenta de la empresa donde depositaremos el crédito y desde donde se domiciliarán los pagos."
                      status={application.bank_information_status}
                      icon={<AccountBalanceOutlined sx={{ fontSize: 18 }} />}
                      label="Llenar"
                      onClick={() => navigate(PATH_INFO_BANCARIA)}
                      disabled={!isFormStatusEditable(application.bank_information_status)}
                      data-testid="expediente-empresa-bancaria-button"
                    />
                  )}
                </Paper>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <DocumentList
                    documents={documentos}
                    onChange={({ document, newValue }) =>
                      dispatch(
                        setExpedienteEmpresaDoc({
                          document,
                          newValue,
                          valueAction: undefined,
                          valueIndex: undefined,
                        }),
                      )
                    }
                    onChangeMultiple={({ document, newValue, valueAction, valueIndex }) =>
                      dispatch(setExpedienteEmpresaDoc({ document, newValue, valueAction, valueIndex }))
                    }
                  />
                </Box>
              </Paper>
            </Box>
          </Box>

          <StepActions
            onBack={() => navigate(`${BASE}?tab=dictamen`)}
            onNext={() => navigate(`${BASE}?tab=${nextTab}`)}
            nextLabel={nextTab === "garantia" ? "Continuar a garantía" : "Continuar a personas"}
            nextDisabled={!isComplete}
            backTestId="expediente-empresa-back-button"
            nextTestId="expediente-empresa-continue-button"
          />
        </Fragment>
      )}
    </Box>
  );
};
